import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import EventCard from '../components/player/EventCard';
import ClubCard from '../components/player/ClubCard';
import FilterBar from '../components/player/FilterBar';
import SearchHero from '../components/player/SearchHero';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import { getEvents, deleteEvent, updateEvent, getOrganizers, getClubs } from '../services/api';
import toast from 'react-hot-toast';

/**
 * EventsFeed - Página Principal de Feed de Pozos
 * 
 * Vista principal donde los jugadores descubren y exploran pozos disponibles.
 * Incluye filtros, búsqueda y lista de eventos.
 */
const EventsFeed = () => {
    const [filters, setFilters] = useState({
        level: '',
        city: '',
        date: 'all',
        organizerId: '',
        clubId: '',
        sort: 'date-asc'
    });

    const isClubSearch = filters.type === 'club';
    console.log('[EventsFeed] filters:', filters, 'isClubSearch:', isClubSearch);

    // Fetch filters data
    const { data: organizers = [] } = useQuery({
        queryKey: ['organizers'],
        queryFn: async () => {
            try {
                const data = await getOrganizers();
                return Array.isArray(data) ? data : (data.users || []);
            } catch (e) {
                console.error("Error fetching organizers", e);
                return [];
            }
        },
        staleTime: 60 * 60 * 1000,
    });

    const { 
        data: clubsData = [], 
        isLoading: isLoadingClubs,
        isError: isErrorClubs,
        error: clubsError
    } = useQuery({
        queryKey: ['clubs', filters],
        queryFn: async () => {
            try {
                // If we are in club search mode, we pass the filters (city, coords, etc)
                const data = await getClubs(isClubSearch ? filters : {});
                return Array.isArray(data) ? data : (data.clubs || []);
            } catch (e) {
                console.error("Error fetching clubs", e);
                throw e;
            }
        },
        // Only run if it's a club search or we need them for filters (FilterBar needs them)
        enabled: true, 
        staleTime: isClubSearch ? 0 : 60 * 60 * 1000,
    });

    const clubs = Array.isArray(clubsData) ? clubsData : [];

    // DEBUG: exposing data to window for browser subagent
    if (typeof window !== 'undefined') {
        window.DEBUG_CLUBS = clubs;
        window.DEBUG_FILTERS = filters;
        window.DEBUG_IS_CLUB_SEARCH = isClubSearch;
    }

    // Fetch events con React Query
    const {
        data: eventsQueryResult,
        isLoading: isLoadingEvents,
        isError: isErrorEvents,
        error: eventsError,
        refetch: refetchEvents
    } = useQuery({
        queryKey: ['events', filters],
        queryFn: () => getEvents(filters),
        enabled: !isClubSearch, // Only fetch events if not searching for clubs
        staleTime: 0, 
    });

    const events = eventsQueryResult?.events || [];
    const pagination = eventsQueryResult?.pagination || {};

    // Global loading/error state based on mode
    const isMainLoading = isClubSearch ? isLoadingClubs : isLoadingEvents;
    const isMainError = isClubSearch ? isErrorClubs : isErrorEvents;
    const mainError = isClubSearch ? clubsError : eventsError;

    // Manejador de cambio de filtros
    const handleFilterChange = (newFiltersOrKey, value) => {
        console.log('[EventsFeed] handleFilterChange called with:', newFiltersOrKey, value);
        if (typeof newFiltersOrKey === 'object') {
             const newFilters = { ...filters, ...newFiltersOrKey };
             console.log('[EventsFeed] Setting filters to:', newFilters);
             setFilters(newFilters);
        } else {
             setFilters(prev => ({ ...prev, [newFiltersOrKey]: value }));
        }
    };

    // Estado para el modal de confirmación
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [eventToDelete, setEventToDelete] = useState(null);

    // Manejador para abrir el modal de borrado
    const handleDelete = (eventId) => {
        setEventToDelete(eventId);
        setDeleteModalOpen(true);
    };

    // Confirmar borrado
    const confirmDelete = async () => {
        if (!eventToDelete) return;

        try {
            await deleteEvent(eventToDelete);
            toast.success('Pozo eliminado correctamente');
            refetch();
        } catch (error) {
            console.error('Error eliminando evento:', error);
            toast.error('Error al eliminar el pozo: ' + error.message);
        } finally {
            setDeleteModalOpen(false);
            setEventToDelete(null);
        }
    };

    // Manejador para subir cartel
    const handleUploadPoster = async (eventId, base64Image) => {
        try {
            await updateEvent(eventId, { posterUrl: base64Image });
            toast.success('Cartel publicitario actualizado');
            refetch();
        } catch (error) {
            console.error('Error subiendo cartel:', error);
            toast.error('Error al subir el cartel: ' + error.message);
        }
    };

    // Scroll to top cuando cambian filtros
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [filters]);

    return (
        <div className="min-h-screen bg-gray-50 relative">
            {/* Modal de Confirmación */}
            {deleteModalOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-in fade-in zoom-in duration-200">
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">¿Eliminar este pozo?</h3>
                            <p className="text-sm text-gray-500 mb-6">
                                Esta acción no se puede deshacer. El evento será cancelado permanentemente.
                            </p>
                            <div className="flex gap-3 justify-center">
                                <button
                                    onClick={() => setDeleteModalOpen(false)}
                                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium shadow-lg shadow-red-500/30 transition-colors"
                                >
                                    Sí, eliminar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Hero Section - Search Engine Style */}
            <SearchHero onSearch={handleFilterChange} />

            {/* Main Content */}
            <div className="container mx-auto px-4 py-8">
                {/* Filtros */}
                <FilterBar
                    onFilterChange={handleFilterChange}
                    initialFilters={filters}
                    organizers={organizers}
                    clubs={clubs}
                />

                {/* Mobile Filter Button */}
                <FilterBar.Mobile
                    onFilterChange={handleFilterChange}
                    initialFilters={filters}
                    organizers={organizers}
                    clubs={clubs}
                />

                {/* Resultados */}
                <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
                        {isMainLoading ? 'Cargando...' :
                            isClubSearch ? 
                                (clubs.length === 0 ? 'No se encontraron clubes' : `${clubs.length} Club${clubs.length === 1 ? '' : 'es'} encontrado${clubs.length === 1 ? '' : 's'}`) :
                                (events.length === 0 ? 'No hay pozos disponibles' : `${events.length} Poz${events.length === 1 ? 'o' : 'os'} disponible${events.length === 1 ? '' : 's'}`)}
                    </h2>

                    {/* Ordenar por */}
                    <div className="w-full sm:w-auto">
                        <select
                            className="w-full sm:w-auto bg-white border-2 border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold focus:border-primary-500 focus:outline-none shadow-sm transition-all"
                            value={filters.sort || (isClubSearch ? 'name-asc' : 'date-asc')}
                            onChange={(e) => handleFilterChange('sort', e.target.value)}
                        >
                            {isClubSearch ? (
                                <>
                                    <option value="name-asc">🔤 Nombre (A-Z)</option>
                                    <option value="name-desc">🔤 Nombre (Z-A)</option>
                                    <option value="distance" disabled={!filters.userCoords}>
                                        🚗 Distancia {!filters.userCoords ? '(Inactivo)' : ''}
                                    </option>
                                </>
                            ) : (
                                <>
                                    <option value="date-asc">📅 Próximos primero</option>
                                    <option value="date-desc">📅 Lejanos primero</option>
                                    <option value="price-asc">💰 Precio: Menor a mayor</option>
                                    <option value="price-desc">💰 Precio: Mayor a menor</option>
                                    <option value="distance" disabled={!filters.userCoords}>
                                        🚗 Distancia {!filters.userCoords ? '(Inactivo)' : ''}
                                    </option>
                                </>
                            )}
                        </select>
                    </div>
                </div>

                {/* Loading State */}
                {isMainLoading && (
                    <div className="flex justify-center items-center py-20" id="results-loading">
                        <LoadingSpinner size="lg" text={isClubSearch ? "Cargando clubes..." : "Cargando pozos..."} />
                    </div>
                )}

                {/* Error State */}
                {isMainError && (
                    <div className="bg-error/10 border-2 border-error rounded-xl p-6 text-center" id="results-error">
                        <p className="text-error font-semibold mb-2">
                            ❌ Error al cargar los resultados
                        </p>
                        <p className="text-gray-600 mb-4">
                            {mainError?.message || 'Ha ocurrido un error inesperado'}
                        </p>
                        <button
                            onClick={() => isClubSearch ? null : refetchEvents()}
                            className="bg-primary-500 hover:bg-primary-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                        >
                            🔄 Reintentar
                        </button>
                    </div>
                )}

                {/* Empty State */}
                {/* Empty State */}
                {!isMainLoading && !isMainError && isClubSearch && clubs.length === 0 && (
                    <div className="bg-white rounded-2xl shadow-card p-12 text-center" id="empty-state-clubs" data-testid="empty-state-clubs">
                        <div className="text-6xl mb-4">🔍</div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">No encontramos clubes</h3>
                        <p className="text-gray-600 mb-6 font-medium">No encontramos clubes que coincidan con tu búsqueda en esta zona.</p>
                        <button
                            onClick={() => setFilters({ level: '', city: '', date: 'all', organizerId: '', clubId: '' })}
                            className="bg-padel-branded text-white font-bold py-4 px-8 rounded-xl shadow-lg hover:brightness-110 transition-all transform hover:-translate-y-1"
                        >
                            Limpiar Filtros
                        </button>
                    </div>
                )}

                {!isMainLoading && !isMainError && !isClubSearch && events.length === 0 && (
                    <div className="bg-white rounded-2xl shadow-card p-12 text-center" id="empty-state-events" data-testid="empty-state-events">
                        <div className="text-6xl mb-4">🔍</div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">No encontramos pozos</h3>
                        <p className="text-gray-600 mb-6 font-medium">No encontramos pozos disponibles en este momento.</p>
                        <button
                            onClick={() => setFilters({ level: '', city: '', date: 'all', organizerId: '', clubId: '' })}
                            className="bg-padel-branded text-white font-bold py-4 px-8 rounded-xl shadow-lg hover:brightness-110 transition-all transform hover:-translate-y-1"
                        >
                            Limpiar Filtros
                        </button>
                    </div>
                )}

                {/* Events Grid */}
                {!isMainLoading && !isMainError && !isClubSearch && events.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" id="events-grid">
                        {events.map((event) => (
                            <EventCard
                                key={event.id}
                                event={event}
                                onDelete={handleDelete}
                                onUploadPoster={handleUploadPoster}
                            />
                        ))}
                    </div>
                )}

                {/* Clubs Grid */}
                {!isMainLoading && !isMainError && isClubSearch && clubs.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" id="clubs-grid">
                        {clubs.map((club) => (
                            <ClubCard
                                key={club.id || (`${club.n}-${club.d}`)}
                                club={club}
                            />
                        ))}
                    </div>
                )}

                {/* Pagination (futuro) */}
                {pagination.pages > 1 && (
                    <div className="mt-8 flex justify-center gap-2">
                        <button className="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                            ← Anterior
                        </button>
                        <div className="flex gap-2">
                            {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => (
                                <button
                                    key={i}
                                    className={`
                    px-4 py-2 rounded-lg font-semibold
                    ${i + 1 === pagination.page
                                            ? 'bg-primary-500 text-white'
                                            : 'bg-white border-2 border-gray-200 hover:bg-gray-50'}
                  `}
                                >
                                    {i + 1}
                                </button>
                            ))}
                        </div>
                        <button className="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                            Siguiente →
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EventsFeed;
